import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ENVIRONMENT_INITIALIZER, ErrorHandler, inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { Route, RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { APPWRITE_CONFIG_TOKEN, AppwriteClientService, BottomSheetModule, ConfirmationDialogModule, DialogModule, LoadingBarModule, LoadingSpinnerModule, SelectModule, SnackbarModule, TooltipModule, VarDirectiveModule } from '@prism/angular-shared';
import { EnvironmentId, PrismModule } from '@prism/prism-shared';
import { NgxMaskModule } from 'ngx-mask';
import { environment } from '../environments/environment';
import { AdminComponent } from './admin/admin.component';
import { AppComponent } from './app.component';
import { BadgeLoginComponent } from './badge-login/badge-login.component';
import { HeaderComponent } from './header/header.component';
import { HomeScreenComponent } from './home-screen/home-screen.component';
import { LoginComponent } from './login/login.component';
import { AuthGuard } from './shared/guards/auth.guard';
import { PadNumberPipe } from './shared/pipes/pad-number/pad-number-pipe';
import { ChunkReloadErrorHandler, ChunkReloadService } from './shared/services/chunk-reload.service';
import { CoreEffects } from './shared/state/core/core.effects';
import { coreReducer } from './shared/state/core/core.reducer';
import { AutomationEffects } from './shared/state/factory/automation/automation.effects';
import { automationReducer } from './shared/state/factory/automation/automation.reducer';
import { MetalsEffects } from './shared/state/factory/metals/metals.effects';
import { metalsReducer } from './shared/state/factory/metals/metals.reducer';
import { PlasticsEffects } from './shared/state/factory/plastics/plastics.effects';
import { plasticsReducer } from './shared/state/factory/plastics/plastics.reducer';
import { ReactiveFormsModule } from '@angular/forms';
import { ClientDeviceInformationModule } from './shared/components/client-device-information-dialog/client-device-information-dialog.module';
import { prismCommonReducer } from './shared/state/factory/common/common.reducer';
import { PrismCommonEffects } from './shared/state/factory/common/common.effects';
import { sharedReducer } from './shared/state/factory/shared/shared.reducer';
import { SharedEffects } from './shared/state/factory/shared/shared.effects';
import { ToUserNameModule } from "./shared/pipes/to-user-name/to-user-name.module";
import { ClipboardModule } from '@angular/cdk/clipboard';
import { CountUpModule } from 'ngx-countup';
import { NoAccessComponent } from './no-access/no-access.component';
import { TimeDeltaPipeModule } from "../../../../libs/angular-shared/src/lib/pipes/time-delta/time-delta.module";
import { PrintFinishingScehduleComponent } from './plastics/roto-management/print-finishing-schedule/print-finishing-scehdule.component';
import { EditSoftwareRequestDialogModule } from './shared/components/edit-software-request-dialog/edit-software-request-dialog.module';
import { BackgroundModule } from './shared/components/background/background.module';
import { c3Reducer } from './shared/state/factory/c3/c3.reducer';
import { C3Effects } from './shared/state/factory/c3/c3.effects';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { WeatherboyModule } from './shared/components/weatherboy/weatherboy.module';
import { SubmoduleCardModule } from './shared/components/submodule-card/submodule-card.module';






export const appRoutes: Route[] = [
  {
    path: PrismModule.Admin,
    component: AdminComponent,
    loadChildren: () =>
      import('./admin/admin.module').then((m) => m.AdminModule),
    canActivate: [
      AuthGuard,
    ],
  },
  {
    path: PrismModule.Plastics,
    loadChildren: () =>
      import('./plastics/plastics.module').then((m) => m.PlasticsModule),
    canActivate: [
      AuthGuard,
    ],
  },
  {
    path: PrismModule.Automation,
    loadChildren: () =>
      import('./automation/automation.module').then((m) => m.AutomationModule),
    canActivate: [
      AuthGuard,
    ],
  },
  {
    path: PrismModule.Settings,
    loadChildren: () =>
      import('./settings/settings.module').then((m) => m.SettingsModule),
    canActivate: [
      AuthGuard
    ]
  },
  {
    path: PrismModule.Metals,
    loadChildren: () =>
      import('./metals/metals.module').then((m) => m.MetalsModule),
    canActivate: [
      AuthGuard,
    ]
  },
  {
    path: PrismModule.PrismCommon,
    loadChildren: () =>
      import('./prism-common/prism-common.module').then((m) => m.PrismCommonModule),
    canActivate: [
      AuthGuard
    ]
  },
  {
    path: PrismModule.C3,
    loadChildren: () =>
      import('./c3/c3.module').then((m) => m.C3Module),
    canActivate: [
      AuthGuard
    ]
  },
  {
    path: PrismModule.InjectionMolding,
    loadChildren: () => import('./injection-molding/injection-molding.module')
      .then((m) => m.InjectionMoldingModule),
    canActivate: [AuthGuard],
  },
  {
    path: PrismModule.Printing3d,
    loadChildren: () => import('./printing-3d/printing-3d.module').then(m => m.Printing3dModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'print-finishing-schedule',
    component: PrintFinishingScehduleComponent,
  },
  {
    path: 'no-access',
    component: NoAccessComponent,
  },
  {
    path: "badge-login",
    component: BadgeLoginComponent,
  },
  {
    path: "",
    component: HomeScreenComponent,
    canActivate: [
      AuthGuard,
    ],
    canActivateChild: [
      AuthGuard,
    ]
  },

];

const stateSlices = {
  core: coreReducer,
  metals: metalsReducer,
  plastics: plasticsReducer,
  automation: automationReducer,
  prismCommon: prismCommonReducer,
  shared: sharedReducer,
  c3: c3Reducer,
};

const effects = [
  CoreEffects,
  MetalsEffects,
  PlasticsEffects,
  AutomationEffects,
  PrismCommonEffects,
  SharedEffects,
  C3Effects,
];

const nonProductionModules = environment.environmentId === EnvironmentId.Production
  ? []
  : [
    StoreDevtoolsModule.instrument({
      name: 'prism',
      maxAge: 25,
    }),
  ];

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HomeScreenComponent,
    LoginComponent,
    BadgeLoginComponent,
    NoAccessComponent,
    PrintFinishingScehduleComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(appRoutes, { initialNavigation: 'enabledBlocking' }),
    StoreModule.forRoot(stateSlices),
    EffectsModule.forRoot(effects),
    SnackbarModule,
    DialogModule,
    BottomSheetModule,
    ConfirmationDialogModule,
    HttpClientModule,
    NgxMaskModule.forRoot(),
    ReactiveFormsModule,
    LoadingBarModule,
    ClientDeviceInformationModule,
    ...nonProductionModules,
    SelectModule,
    ToUserNameModule,
    ClipboardModule,
    CountUpModule,
    TimeDeltaPipeModule,
    VarDirectiveModule,
    EditSoftwareRequestDialogModule,
    TooltipModule,
    BackgroundModule,
    LoadingSpinnerModule,
    WeatherboyModule,
    SubmoduleCardModule,
  ],
  providers: [
    PadNumberPipe,
    { provide: APPWRITE_CONFIG_TOKEN, useValue: environment.appwrite },
    AppwriteClientService,
    // Stale tabs after a deploy: reload into the new build at the route they wanted
    { provide: ErrorHandler, useClass: ChunkReloadErrorHandler },
    { provide: ENVIRONMENT_INITIALIZER, multi: true, useValue: () => inject(ChunkReloadService) },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
